import type {
  AgendaItem,
  ActionItem as StitchWebActionItem,
} from "@elation/api-client/src/stitch-web";

import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Checkbox } from "@src/shared/components/ui/checkbox";
import { cn } from "@src/shared/lib/utils";

import { ActionItemSection, sectionTheme } from "./ActionItems.types";

export interface ActionItemProps {
  actionItem: StitchWebActionItem;
  section: ActionItemSection;
  agendaItem: AgendaItem | null;
  /** Toggle handler hoisted to the section so one mutation hook serves the list. */
  onToggle: (item: StitchWebActionItem) => void;
}

export const ActionItem = ({
  actionItem,
  section,
  agendaItem,
  onToggle,
}: ActionItemProps) => {
  const theme = sectionTheme[section];
  const checked = actionItem.status === "completed";

  const toggle = () => {
    onToggle(actionItem);
  };

  return (
    <li>
      <HorizontalFlexbox
        as="button"
        type="button"
        alignItems="flex-start"
        gap="sm"
        onClick={toggle}
        className={cn(
          "w-full rounded-lg px-2.5 py-2 text-left transition-colors",
          "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          theme.card,
          checked && theme.cardActive,
        )}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={toggle}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            "mt-0.5 border-primary shadow-none data-[state=checked]:text-primary-foreground",
            theme.checkbox,
          )}
        />
        <VerticalFlexbox gap="none">
          <Text
            sizing="sm"
            fontWeight={500}
            appearance={checked ? "subdued" : "default"}
            className={cn(checked && "line-through")}
          >
            {actionItem.summary}
          </Text>
          {agendaItem ? (
            <Text sizing="xs" appearance="subdued">
              {agendaItem.description}
            </Text>
          ) : null}
        </VerticalFlexbox>
      </HorizontalFlexbox>
    </li>
  );
};
